import { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Badge, Button, Modal, Spinner, Table } from 'react-bootstrap';
import { notify } from 'messages';

import {
  getNodeInstanceBlockers,
  unblockNodeInstanceBlocker,
  type NodeInstanceBlocker,
  type NodeInstanceBlockerSummary,
} from 'api/node_instances/endpoints/getNodeInstanceBlockers';
import { extractErrorMessage } from 'helpers/extractErrorMessage';
import { acceptDegraded } from 'validation';

interface Props {
  readonly show: boolean;
  readonly nodeInstanceId: number | string | null;
  readonly onHide: () => void;
  readonly onChanged?: () => void;
}

const blockerLabel = (blocker: NodeInstanceBlocker): string => {
  if (blocker.type === 'event_wait') return 'Event being waited on';
  if (blocker.type === 'scheduled_start_datetime') return 'Scheduled run time';
  return 'Scheduled start time';
};

const eventDetails = (blocker: NodeInstanceBlocker) => {
  if (blocker.type !== 'event_wait') return null;
  const details = blocker.details;
  const path = [details.zone_name, details.event_name].filter(Boolean).join(' | ');
  return (
    <div className="d-flex flex-column gap-1">
      <span>{path || 'Unknown event'}</span>
      <div className="d-flex gap-1 flex-wrap">
        {details.event_type && (
          <Badge bg="light" text="dark">
            {details.event_type}
          </Badge>
        )}
        {details.auto_submit && (
          <Badge bg="light" text="dark">
            Auto-submit
          </Badge>
        )}
        <Badge bg={details.raised ? 'success' : 'secondary'}>{details.raised ? 'Raised' : 'Not raised'}</Badge>
        {details.precondition && (
          <Badge bg="light" text="dark">
            Precondition
          </Badge>
        )}
      </div>
    </div>
  );
};

const scheduleDetails = (blocker: NodeInstanceBlocker) => {
  if (blocker.type !== 'schedule_start_time') return null;
  const details = blocker.details;
  const scheduleText = details.target_time || details.schedule_start_time || 'Unknown time';
  return (
    <div className="d-flex flex-column gap-1">
      <span>
        {scheduleText}
        {details.timezone ? ` ${details.timezone}` : ''}
      </span>
      {details.current_time && (
        <small className="text-muted">
          Current time: {details.current_time}
          {details.timezone ? ` ${details.timezone}` : ''}
        </small>
      )}
    </div>
  );
};

const scheduledDatetimeDetails = (blocker: NodeInstanceBlocker) => {
  if (blocker.type !== 'scheduled_start_datetime') return null;
  const details = blocker.details;
  const scheduleText = details.target_time || details.scheduled_start_datetime || 'Unknown time';
  return (
    <div className="d-flex flex-column gap-1">
      <span>
        {scheduleText}
        {details.timezone ? ` ${details.timezone}` : ''}
      </span>
      {details.target_datetime && <small className="text-muted">{details.target_datetime}</small>}
      {details.current_time && (
        <small className="text-muted">
          Current time: {details.current_time}
          {details.timezone ? ` ${details.timezone}` : ''}
        </small>
      )}
    </div>
  );
};

const blockerDetails = (blocker: NodeInstanceBlocker) => {
  if (blocker.type === 'event_wait') return eventDetails(blocker);
  if (blocker.type === 'scheduled_start_datetime') return scheduledDatetimeDetails(blocker);
  return scheduleDetails(blocker);
};

const instanceLabel = (nodeInstance: NodeInstanceBlockerSummary | null, fallbackId: number | string | null): string => {
  const id = nodeInstance?.node_instance_id ?? fallbackId;
  const name = nodeInstance?.name;
  return [id ? `#${id}` : null, name].filter(Boolean).join(' - ');
};

const effectiveStatusLabel = (nodeInstance: NodeInstanceBlockerSummary | null, activeBlockers: readonly NodeInstanceBlocker[]): string => {
  if (!nodeInstance?.status) return '';

  const hasScheduleBlocker = activeBlockers.some((blocker) => blocker.type === 'scheduled_start_datetime' || blocker.type === 'schedule_start_time');
  const hasEventBlocker = activeBlockers.some((blocker) => blocker.type === 'event_wait');

  if (!hasScheduleBlocker && hasEventBlocker) {
    return 'eventWait';
  }

  return nodeInstance.status;
};

const UnblockNodeInstanceDialog = ({ show, nodeInstanceId, onHide, onChanged }: Props) => {
  const [blockers, setBlockers] = useState<readonly NodeInstanceBlocker[]>([]);
  const blockersRef = useRef<readonly NodeInstanceBlocker[]>([]);
  const [clearedBlockerIds, setClearedBlockerIds] = useState<ReadonlySet<string>>(new Set());
  const [nodeInstance, setNodeInstance] = useState<NodeInstanceBlockerSummary | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [busyBlockerId, setBusyBlockerId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!nodeInstanceId) return;
    setIsLoading(true);
    setError(null);
    try {
      const response = acceptDegraded(await getNodeInstanceBlockers(nodeInstanceId));
      setNodeInstance(response.node_instance);
      blockersRef.current = response.blockers;
      setBlockers(response.blockers);
      setClearedBlockerIds(new Set());
    } catch (loadError: any) {
      setError(extractErrorMessage(loadError.response?.data, loadError.message));
    } finally {
      setIsLoading(false);
    }
  }, [nodeInstanceId]);

  useEffect(() => {
    if (!show) {
      blockersRef.current = [];
      setBlockers([]);
      setNodeInstance(null);
      setError(null);
      setBusyBlockerId(null);
      setClearedBlockerIds(new Set());
      return;
    }
    void load();
  }, [load, show]);

  const mergeBlockers = (current: readonly NodeInstanceBlocker[], remaining: readonly NodeInstanceBlocker[]) => {
    const existingIds = new Set(current.map((blocker) => blocker.id));
    const remainingById = new Map(remaining.map((blocker) => [blocker.id, blocker]));
    const merged = current.map((blocker) => remainingById.get(blocker.id) || blocker);
    remaining.forEach((blocker) => {
      if (!existingIds.has(blocker.id)) {
        merged.push(blocker);
      }
    });
    return merged;
  };

  const handleUnblock = async (blocker: NodeInstanceBlocker) => {
    if (!nodeInstanceId || !blocker.actionable || clearedBlockerIds.has(blocker.id)) return;
    setBusyBlockerId(blocker.id);
    setError(null);
    try {
      const response = acceptDegraded(await unblockNodeInstanceBlocker(nodeInstanceId, blocker.id));
      notify.success(response.message || 'Blocker unblocked');
      let latestRemainingBlockers = response.remaining_blockers;
      try {
        const latest = acceptDegraded(await getNodeInstanceBlockers(nodeInstanceId));
        latestRemainingBlockers = latest.blockers;
        setNodeInstance(latest.node_instance);
      } catch {
        // The unblock succeeded; keep the PUT result if the follow-up status refresh fails.
      }
      const mergedBlockers = mergeBlockers(blockersRef.current, latestRemainingBlockers);
      blockersRef.current = mergedBlockers;
      setBlockers(mergedBlockers);
      setClearedBlockerIds((current) => {
        const remainingIds = new Set(latestRemainingBlockers.map((remainingBlocker) => remainingBlocker.id));
        const next = new Set(current);
        mergedBlockers.forEach((currentBlocker) => {
          if (!remainingIds.has(currentBlocker.id)) {
            next.add(currentBlocker.id);
          }
        });
        next.add(blocker.id);
        return next;
      });
      onChanged?.();
    } catch (unblockError: any) {
      const message = extractErrorMessage(unblockError.response?.data, unblockError.message);
      setError(message);
      notify.error(message || 'Failed to unblock node instance');
    } finally {
      setBusyBlockerId(null);
    }
  };

  const currentInstanceLabel = instanceLabel(nodeInstance, nodeInstanceId);
  const activeBlockers = blockers.filter((blocker) => !clearedBlockerIds.has(blocker.id));
  const currentStatus = effectiveStatusLabel(nodeInstance, activeBlockers);
  const allBlockersCleared = blockers.length > 0 && activeBlockers.length === 0;

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Unblock node instance</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {currentInstanceLabel && (
          <div className="d-flex align-items-center gap-2 flex-wrap mb-3">
            <span className="fw-semibold">{currentInstanceLabel}</span>
            {currentStatus && (
              <span className="d-inline-flex align-items-center gap-1 text-muted small">
                Current status:
                <Badge bg="light" text="dark">
                  {currentStatus}
                </Badge>
              </span>
            )}
          </div>
        )}

        {error && <Alert variant="danger">{error}</Alert>}
        {allBlockersCleared && <Alert variant="success">All blockers removed.</Alert>}

        {isLoading ? (
          <div className="d-flex align-items-center gap-2 py-3">
            <Spinner animation="border" size="sm" />
            <span>Loading blockers...</span>
          </div>
        ) : blockers.length === 0 ? (
          <Alert variant="info" className="mb-0">
            No active blockers.
          </Alert>
        ) : (
          <Table responsive bordered hover className="mb-0 align-middle">
            <thead>
              <tr>
                <th>Blocker</th>
                <th>Details</th>
                <th className="text-end">Action</th>
              </tr>
            </thead>
            <tbody>
              {blockers.map((blocker) => {
                const isCleared = clearedBlockerIds.has(blocker.id);
                return (
                  <tr key={blocker.id}>
                    <td>{blockerLabel(blocker)}</td>
                    <td>{blockerDetails(blocker)}</td>
                    <td className="text-end">
                      {isCleared ? (
                        <Badge bg="success">Unblocked</Badge>
                      ) : (
                        <Button
                          variant="outline-primary"
                          size="sm"
                          disabled={!blocker.actionable || busyBlockerId !== null}
                          onClick={() => handleUnblock(blocker)}
                        >
                          {busyBlockerId === blocker.id ? 'Unblocking...' : 'Unblock'}
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default UnblockNodeInstanceDialog;
